<template>
  <div class="card">
    <div class="card-header">
      <h5>{{ $t('profile.account.generalSettings') }}</h5>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="text-center py-4">
        <span class="spinner-border" role="status"></span>
        <p class="mt-2 mb-0">Loading company profile...</p>
      </div>
      <form v-else @submit.prevent="onSubmit">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="displayName" class="form-label">{{ $t('profile.fields.company') }} <span class="text-danger">*</span></label>
            <input 
              id="displayName"
              type="text" 
              class="form-control" 
              v-model="formData.displayName" 
              :placeholder="$t('profile.fields.company')"
              :disabled="!canEditTenantInfo" 
            />
            <div class="form-text">
              Company display name for public visibility
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label for="email" class="form-label">{{ $t('profile.fields.email') }}</label>
            <input 
              id="email"
              type="email" 
              class="form-control" 
              v-model="formData.email" 
              :placeholder="$t('profile.fields.email')"
              :disabled="!canEditTenantInfo" 
            />
            <div class="form-text">Company contact email address</div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="phone" class="form-label">{{ $t('profile.fields.phone') }}</label>
            <input 
              id="phone"
              type="tel" 
              class="form-control" 
              v-model="formData.phone" 
              :placeholder="$t('profile.fields.phone')"
              :disabled="!canEditTenantInfo" 
            />
            <div class="form-text">Company contact phone number</div>
          </div>
        </div>
        
        <div class="text-end mt-4" v-if="canEditTenantInfo">
          <button type="submit" class="btn btn-primary" :disabled="isSaving">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('common.save') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'ProfileAccountForm',
  props: {
    profile: {
      type: Object,
      required: true
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['submit', 'cancel'],
  setup(props, { emit }) {
    const store = useStore();
    const { hasPermission } = usePermissions();
    
    // Permission check
    const canEditTenantInfo = computed(() => hasPermission('Supplier.TenantInfo.Set'));
    
    // Create a reactive form data object for tenant extra properties
    const formData = reactive({
      displayName: props.profile.displayName || '',
      email: props.profile.email || '',
      phone: props.profile.phone || '',
      about: props.profile.about || '',
      companyLogoId: props.profile.companyLogoId || ''
    });
    
    // Phone input with simple masking
    const phoneInput = ref(formData.phone || '');
    
    // Watch for prop changes to update form data
    watch(() => props.profile, (newProfile) => {
      formData.displayName = newProfile.displayName || '';
      formData.email = newProfile.email || '';
      formData.phone = newProfile.phone || '';
      formData.about = newProfile.about || '';
      formData.companyLogoId = newProfile.companyLogoId || '';
      phoneInput.value = newProfile.phone || '';
    }, { deep: true });
    
    // Watch for phone input changes to update the form data
    watch(phoneInput, (newValue) => {
      // Store the formatted phone number
      formData.phone = newValue;
    });
    
    // Format phone number when input loses focus
    const formatPhoneOnBlur = () => {
      if (!phoneInput.value) return;

      // Clean the input of any non-numeric characters
      let digitsOnly = phoneInput.value.replace(/\D/g, '');

      // Remove leading country code digits if present
      if (digitsOnly.startsWith('90')) {
        digitsOnly = digitsOnly.substring(2);
      }

      // Only format if we have some digits
      if (digitsOnly.length > 0) {
        // Always prefix with country code
        let formatted = '+90 ';

        // Add the rest of the number with simple spacing
        if (digitsOnly.length <= 3) {
          formatted += digitsOnly;
        } else if (digitsOnly.length <= 6) {
          formatted += digitsOnly.substring(0, 3) + ' ' + digitsOnly.substring(3);
        } else if (digitsOnly.length <= 8) {
          formatted += digitsOnly.substring(0, 3) + ' ' + digitsOnly.substring(3, 6) + ' ' + digitsOnly.substring(6);
        } else {
          formatted += digitsOnly.substring(0, 3) + ' ' + digitsOnly.substring(3, 6) + ' ' +
                       digitsOnly.substring(6, 8) + ' ' + digitsOnly.substring(8);
        }

        phoneInput.value = formatted;
      }
    };
    
    // Validate keypress to only allow numbers and some special characters
    const validatePhoneKeypress = (event) => {
      // Allow: backspace, delete, tab, escape, enter, +, space, (, ), -
      const allowedKeys = [8, 9, 27, 13, 32, 40, 41, 43, 45];
      
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (event.ctrlKey && [65, 67, 86, 88].includes(event.keyCode)) {
        return true;
      }
      
      // Allow numbers
      if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105)) {
        return true;
      }
      
      // Allow special characters for phone numbers
      if (allowedKeys.includes(event.keyCode)) {
        return true;
      }
      
      // Prevent the key press if not allowed
      event.preventDefault();
      return false;
    };
    
    // Submit form data
    const onSubmit = async () => {
      try {
        // Format the phone number before submitting
        formatPhoneOnBlur();
        
        // Submit the tenant extra properties
        const updateData = {
          displayName: formData.displayName,
          email: formData.email,
          phone: formData.phone,
          about: formData.about,
          companyLogoId: formData.companyLogoId
        };
        
        // Emit the updated data to parent component for API call
        emit('submit', updateData);
        
      } catch (error) {
        console.error('Failed to prepare form data:', error);
        store.dispatch('notify', {
          message: 'Failed to prepare form data',
          type: 'error'
        });
      }
    };
    
    return {
      formData,
      phoneInput,
      formatPhoneOnBlur,
      validatePhoneKeypress,
      onSubmit,
      canEditTenantInfo
    };
  }
};
</script>

<style scoped>
.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}
</style> 